import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import type { NurseWithRelation, NurseResponse } from "@/types/nurse-type";
import { compareNurses } from "@/utils/sort-util";
import { nurseRoleSortIndex, rowActiveSortIndex } from "@/utils/common-util";
import { useQueryClient } from "@tanstack/react-query";
import { useIsAdmin } from "./useAuth";
import { useGroupSelection } from "./useGroupSelection";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import type { NurseSequenceReorderRequest } from "@/api/nurses";
import {
  enqueueRosterNurseSequenceSave,
  flushRosterNurseSequenceOnPageHide,
  persistRosterNurseSequenceSave,
  replayPersistedRosterNurseSequenceSave,
  waitForRosterNurseSequenceQueue,
} from "@/pages/roster-create/services/rosterNurseSequenceSaveQueue";

type SortKey = "experience" | "grade" | "name";
type SortOrder = "asc" | "desc" | null;

export interface SortConfig {
  key: SortKey | null;
  order: SortOrder;
}

type NurseSortableOptions = {
  enableSequenceUpdate?: boolean;
  debounceMs?: number;
  keepRoleGroupOrder?: boolean;
  groupId?: string | null;
};

function isInactiveNurse(nurse: Pick<NurseResponse, "active">): boolean {
  return nurse.active === 0;
}

function firstSortOrderForKey(key: SortKey): Exclude<SortOrder, null> {
  return key === "name" ? "asc" : "desc";
}

function buildSequencePayload(
  nurses: readonly NurseResponse[]
): NurseSequenceReorderRequest {
  return {
    active_order: nurses
      .filter((nurse) => !isInactiveNurse(nurse))
      .map((nurse) => nurse.nurse_id),
    inactive_order: nurses
      .filter(isInactiveNurse)
      .map((nurse) => nurse.nurse_id),
  };
}

export function useSortableNurses<T extends NurseWithRelation>(
  nurses: T[],
  options?: NurseSortableOptions
) {
  const {
    enableSequenceUpdate = true,
    debounceMs = 500,
    keepRoleGroupOrder = false,
    groupId: optionGroupId,
  } = options ?? {};

  const queryClient = useQueryClient();
  const isAdmin = useIsAdmin();
  const { selectedGroupId } = useGroupSelection();
  const queryGroupId = optionGroupId ?? selectedGroupId ?? null;
  const sequenceGroupId = isAdmin ? queryGroupId : null;

  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: null,
    order: null,
  });

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingUpdateRef = useRef<NurseResponse[] | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const invalidateSequenceCaches = useCallback(
    () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.nurses.listScope(queryGroupId),
        }),
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.nurses.fullSchema(queryGroupId),
        }),
      ]),
    [queryClient, queryGroupId]
  );

  const sortedNurses = useMemo(() => {
    const { key, order } = sortConfig;
    if (!key || !order) return nurses;

    const sorter = (a: T, b: T) => {
      if (keepRoleGroupOrder) {
        const activeDiff = rowActiveSortIndex(a) - rowActiveSortIndex(b);
        if (activeDiff !== 0) return activeDiff;

        const roleDiff =
          nurseRoleSortIndex(a.role) - nurseRoleSortIndex(b.role);
        if (roleDiff !== 0) return roleDiff;
      }
      return compareNurses(a, b, key, order);
    };

    return [...nurses].sort(sorter);
  }, [nurses, sortConfig, keepRoleGroupOrder]);

  const executeSequenceReorder = useCallback(
    async (nursesToUpdate: NurseResponse[]) => {
      if (nursesToUpdate.length === 0) return;

      setIsUpdating(true);
      try {
        await enqueueRosterNurseSequenceSave(
          buildSequencePayload(nursesToUpdate),
          sequenceGroupId
        );
        await invalidateSequenceCaches();
      } catch (error) {
        console.error("Sequence update error:", error);
        await waitForRosterNurseSequenceQueue(sequenceGroupId);
        await invalidateSequenceCaches();
      } finally {
        setIsUpdating(false);
        pendingUpdateRef.current = null;
      }
    },
    [invalidateSequenceCaches, sequenceGroupId]
  );

  const scheduleSequenceReorder = useCallback(
    (nursesToUpdate: NurseResponse[]) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      pendingUpdateRef.current = nursesToUpdate;
      persistRosterNurseSequenceSave(
        buildSequencePayload(nursesToUpdate),
        sequenceGroupId
      );

      debounceTimerRef.current = setTimeout(() => {
        if (pendingUpdateRef.current) {
          executeSequenceReorder(pendingUpdateRef.current);
        }
      }, debounceMs);
    },
    [debounceMs, executeSequenceReorder, sequenceGroupId]
  );

  const handleSort = useCallback(
    (key: SortKey) => {
      if (isUpdating) return;

      setSortConfig((prev) => {
        let newConfig: SortConfig;

        if (prev.key !== key) {
          newConfig = { key, order: firstSortOrderForKey(key) };
        } else if (prev.order === "asc") {
          newConfig = { key, order: "desc" };
        } else if (prev.order === "desc") {
          newConfig = { key: null, order: null };
        } else {
          newConfig = { key, order: "asc" };
        }

        const { key: nextKey, order: nextOrder } = newConfig;
        if (!nextKey || !nextOrder) {
          return newConfig;
        }

        if (enableSequenceUpdate && nurses.length > 0) {
          const sorter = (a: T, b: T) => {
            if (keepRoleGroupOrder) {
              const activeDiff = rowActiveSortIndex(a) - rowActiveSortIndex(b);
              if (activeDiff !== 0) return activeDiff;

              const roleDiff =
                nurseRoleSortIndex(a.role) - nurseRoleSortIndex(b.role);
              if (roleDiff !== 0) return roleDiff;
            }
            return compareNurses(
              a,
              b,
              nextKey,
              nextOrder
            );
          };
          const sorted = [...nurses].sort(sorter);

          const updatedNurses: NurseResponse[] = sorted.map(
            (nurse, index) =>
              ({
                ...nurse,
                sequence: index + 1,
              })
          );

          queryClient.setQueriesData<NurseResponse[]>(
            { queryKey: QUERY_KEYS.nurses.listScope(queryGroupId) },
            (old) => {
              if (!old) return old;

              const sortedIds = new Set(updatedNurses.map((n) => n.nurse_id));

              const others = old.filter((n) => !sortedIds.has(n.nurse_id));

              return [...updatedNurses, ...others];
            }
          );
          queryClient.setQueryData<NurseResponse[] | undefined>(
            QUERY_KEYS.nurses.fullSchema(queryGroupId),
            (old) => {
              if (!old) return old;

              const sequenceById = new Map(
                updatedNurses.map((nurse) => [nurse.nurse_id, nurse.sequence])
              );

              return [...old]
                .map((nurse) => {
                  const sequence = sequenceById.get(nurse.nurse_id);
                  return sequence == null ? nurse : { ...nurse, sequence };
                })
                .sort((a, b) => a.sequence - b.sequence);
            }
          );

          scheduleSequenceReorder(updatedNurses);
        }

        return newConfig;
      });
    },
    [
      enableSequenceUpdate,
      keepRoleGroupOrder,
      nurses,
      queryClient,
      queryGroupId,
      scheduleSequenceReorder,
      isUpdating,
    ]
  );

  const resetSort = useCallback(() => {
    setSortConfig({ key: null, order: null });
  }, []);

  useEffect(() => {
    const flushPendingSequence = () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      if (pendingUpdateRef.current) {
        const request = flushRosterNurseSequenceOnPageHide(
          buildSequencePayload(pendingUpdateRef.current),
          sequenceGroupId
        );
        if (request) {
          pendingUpdateRef.current = null;
          void request
            .then(() => invalidateSequenceCaches())
            .catch((error) => {
              console.error("Nurse sequence lifecycle save failed:", error);
            });
        }
      }
    };
    window.addEventListener("pagehide", flushPendingSequence);
    return () => {
      window.removeEventListener("pagehide", flushPendingSequence);
      flushPendingSequence();
    };
  }, [invalidateSequenceCaches, sequenceGroupId]);

  useEffect(() => {
    void replayPersistedRosterNurseSequenceSave(sequenceGroupId)
      .then((replayed) => {
        if (replayed) return invalidateSequenceCaches();
      })
      .catch((error) => {
        console.error("Nurse sequence replay failed:", error);
      });
  }, [invalidateSequenceCaches, sequenceGroupId]);

  return {
    sortedNurses,
    sortConfig,
    handleSort,
    resetSort,
    isUpdating,
  };
}
